import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLElement} [cardGalleryWrapper] - The card gallery wrapper component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the slideshow component, falling back to querySelector if not in refs.
   * @returns {any} The slideshow component or null.
   */
  get slideshow() {
    return /** @type {any} */ (this.refs.slideshow || this.querySelector('[ref="slideshow"]'));
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  /** @type {Array<{name: string, value: string}>} Stores selected option name-value pairs before fetch to restore after morph */
  #pendingSelectedValues = [];

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Keep the sale message in sync with the selected color swatch.
    this.addEventListener('change', this.#handleColorChangeForSaleMessage);
    this.#updateSaleMessage();

    // Store selected values BEFORE fetch happens (variantSelected fires before fetch)
    this.addEventListener(ThemeEvents.variantSelected, this.#storeSelectedValuesBeforeFetch);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  /**
   * Stores selected option values before fetch/morph happens.
   * This fires on variantSelected which occurs BEFORE the fetch.
   */
  #storeSelectedValuesBeforeFetch = () => {
    this.#pendingSelectedValues = this.#getSelectedOptionValues();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    // Remove event listeners added in connectedCallback to prevent memory leaks
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('change', this.#handleColorChangeForSaleMessage);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    // Remove the listener that stores selected values before a fetch — previously missing and caused a memory leak
    this.removeEventListener(ThemeEvents.variantSelected, this.#storeSelectedValuesBeforeFetch);

    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    // Remove any quick-add related listeners
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    // Remove media query listener attached to the global mediaQueryLarge object
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
  }

  #preloadNextPreviewImage() {
    const slideshow = this.slideshow;
    const currentSlide = slideshow?.slides?.[slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Always try to morph the gallery if new HTML contains one
    this.#updateGallery(event);

    // Restore selected options after morph using values stored BEFORE fetch
    if (this.#pendingSelectedValues.length > 0) {
      this.#restoreSelectedOptions(this.#pendingSelectedValues);
      this.#pendingSelectedValues = [];
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    this.#updateSaleMessage();

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Re-syncs the sale message when a color swatch changes.
   * @param {Event} event - The change event.
   */
  #handleColorChangeForSaleMessage = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'radio' && /color/i.test(target.name)) {
      this.#updateSaleMessage();
    }
  };

  /**
   * Updates the sale message below the price from the selected color swatch's
   * `data-sale-message` / `data-sale-message-color` attributes. Hides the element
   * when the selected color has no message.
   */
  #updateSaleMessage() {
    // Searchspring cards render an inner `<product-card class="searchspring-product-card">`
    // that this component upgrades. Those cards manage their own sale message (button-based
    // swatches, no radio inputs), so bail out to avoid clobbering it.
    if (this.classList.contains('searchspring-product-card')) return;

    const messageEl = this.refs.saleMessage || this.querySelector('.product-card__sale-message');
    if (!(messageEl instanceof HTMLElement)) return;

    const checkedColor = /** @type {HTMLInputElement | null} */ (
      this.querySelector(
        'input[type="radio"][name*="Color"]:checked, input[type="radio"][name*="color"]:checked'
      )
    );
    const html = checkedColor?.dataset.saleMessage;

    if (!html) {
      messageEl.innerHTML = '';
      messageEl.hidden = true;
      messageEl.style.removeProperty('color');
      return;
    }

    messageEl.innerHTML = html;
    const color = checkedColor?.dataset.saleMessageColor;
    if (color) {
      messageEl.style.color = color;
    } else {
      messageEl.style.removeProperty('color');
    }
    messageEl.hidden = false;
  }

  /**
   * Gets the currently selected option values.
   * @returns {Array<{name: string, value: string}>} Array of selected option name-value pairs
   */
  #getSelectedOptionValues() {
    const checkedInputs = this.querySelectorAll('input[type="radio"]:checked');
    return Array.from(checkedInputs).map((input) => {
      const inputElement = /** @type {HTMLInputElement} */ (input);
      return {
        name: inputElement.name,
        value: inputElement.value
      };
    });
  }

  /**
   * Restores selected options after morph by checking inputs with matching name and value.
   * @param {Array<{name: string, value: string}>} options - Array of option name-value pairs to restore
   */
  #restoreSelectedOptions(options) {
    for (const option of options) {
      const input = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`input[type="radio"][name="${CSS.escape(option.name)}"][value="${CSS.escape(option.value)}"]`)
      );
      if (input && !input.checked) {
        input.checked = true;
      }
    }
  }

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
 * Updates the card gallery when a combined listing product changes.
 * @param {VariantUpdateEvent} event - The variant update event.
 */
  #updateGallery(event) {
    // Find the slideshow element directly - more targeted than replacing entire gallery
    const currentSlideshow = this.slideshow;
    const newSlideshow = event.detail.data.html?.querySelector('[ref="slideshow"]');

    if (currentSlideshow && newSlideshow) {
      // Skip replacement if gallery content is identical (common for non-combined-listing products)
      if (currentSlideshow.innerHTML === newSlideshow.innerHTML) return;

      // Replace just the slideshow element to preserve gallery wrapper styles
      currentSlideshow.replaceWith(newSlideshow.cloneNode(true));

      // Re-cache refs after update
      this.updatedCallback();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    // Query the DOM first to ensure we get a fresh slideshow element after gallery updates
    const slideshow = this.slideshow;

    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (!slideshow || !selectedImageId) return;

    // Prefer an explicit slides array from the component instance if present, otherwise use querySelectorAll
    const slides =
      Array.isArray(slideshow.refs?.slides) ? slideshow.refs.slides : (slideshow.querySelectorAll?.('[slide-id]') || []);

    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;

      slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
    }

    // Prefer calling the slideshow's select method if present. Otherwise, try to bring the slide into view.
    if (typeof slideshow.select === 'function') {
      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    } else {
      const targetSlide = Array.from(slides).find((s) => s.getAttribute('slide-id') === selectedImageId);
      if (targetSlide?.scrollIntoView) {
        targetSlide.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    // Query slideshow directly to ensure fresh reference after gallery updates
    const slideshow = this.slideshow;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select?.({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    // Query slideshow directly to ensure fresh reference after gallery updates
    const slideshow = this.slideshow;

    if (!slideshow) return;

    this.resetVariant.cancel();

    slideshow.next?.(undefined, { animate: false });
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    // Query slideshow directly to ensure fresh reference after gallery updates
    const slideshow = this.slideshow;

    if (!this.variantPicker) {
      if (!slideshow) return;
      const firstSlide = slideshow.querySelector?.('[slide-id]');
      const firstSlideId = firstSlide?.getAttribute('slide-id');
      if (firstSlideId) {
        slideshow.select?.({ id: firstSlideId }, undefined, { animate: false });
      }
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const slideshow = this.slideshow;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        const targetSlide = slideshow.querySelector?.(`[slide-id="${id}"]`);
        if (targetSlide) {
          slideshow.select?.({ id }, undefined, { animate: false });
          return;
        }
      }
    }

    // Fallback: select the first slide
    const firstSlide = slideshow.querySelector?.('[slide-id]');
    const firstSlideId = firstSlide?.getAttribute('slide-id');
    if (firstSlideId) {
      slideshow.select?.({ id: firstSlideId }, undefined, { animate: false });
      return;
    }

    // Last resort - go to previous
    slideshow.previous?.(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @extends {VariantPicker}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Applies embedded swatch data attributes directly to the card — gallery images, size
   * swatches, and the product card link — without making an API fetch.
   * Used when a color swatch has `data-gallery` present.
   * @param {HTMLInputElement} swatchInput - The clicked color swatch input element.
   */
  #applySwatchDataLocally(swatchInput) {
    const productCard = this.parentProductCard;

    // 1. Update product card link and gallery link
    const rtsUrl = swatchInput.dataset.rtsUrl;
    const mtoUrl = swatchInput.dataset.mtoUrl;
    const productUrl = rtsUrl || mtoUrl;
    if (productUrl && productCard instanceof ProductCard) {
      productCard.refs.productCardLink.href = productUrl;
      const galleryLink = /** @type {HTMLAnchorElement | null} */ (
        productCard.querySelector('[ref="cardGalleryLink"]')
      );
      if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = productUrl;
    }

    // 2. Swap gallery images from data-gallery
    const galleryData = swatchInput.dataset.gallery;
    if (galleryData && productCard) {
      try {
        const galleryItems = JSON.parse(galleryData);
        this.#swapGalleryImages(galleryItems, /** @type {ProductCard} */(productCard));
      } catch {
        // Ignore JSON parse errors — fall back to stale images
      }
    }

    // 3. Rebuild size swatches from data-rts-sizes / data-mto-sizes
    const rtsSizes = swatchInput.dataset.rtsSizes;
    const mtoSizes = swatchInput.dataset.mtoSizes;
    if (rtsSizes || mtoSizes) {
      this.#updateSizeSwatches(rtsSizes, mtoSizes);

      // The preserved/preselected size determines whether the RTS or MTO URL applies
      const sizeAwareUrl = this.#getProductUrlForSelectedOptions();
      if (sizeAwareUrl && productCard instanceof ProductCard) {
        productCard.refs.productCardLink.href = sizeAwareUrl;
        const galleryLink = /** @type {HTMLAnchorElement | null} */ (
          productCard.querySelector('[ref="cardGalleryLink"]')
        );
        if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = sizeAwareUrl;
      }
    }

    // 4. Update price display from data-prices ("price|compare_at_price" in dollars)
    const pricesData = swatchInput.dataset.prices;
    if (pricesData && productCard) {
      const pipeIdx = pricesData.indexOf('|');
      const price = pipeIdx !== -1 ? pricesData.slice(0, pipeIdx) : pricesData;
      const compareAt = pipeIdx !== -1 ? pricesData.slice(pipeIdx + 1) : '0';
      this.#updatePriceFromSwatchData(price, compareAt);
    }
  }

  /**
   * Updates slideshow slide images using items from a color swatch's `data-gallery` attribute.
   * Each item has the format `"index:-:url"`. Slides are updated in order; excess slides are hidden.
   * @param {string[]} galleryItems - Ordered array of "index:-:url" strings.
   * @param {ProductCard} productCard - The parent product card element.
   */
  #swapGalleryImages(galleryItems, productCard) {
    const slideshow = productCard.slideshow;
    if (!slideshow) return;

    const slides = Array.isArray(slideshow.refs?.slides)
      ? slideshow.refs.slides
      : Array.from(slideshow.querySelectorAll('[slide-id]'));

    if (!slides.length) return;

    const widths = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
    const maxWidth = widths[widths.length - 1];

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const item = galleryItems[i];

      if (!item) {
        slide.hidden = true;
        continue;
      }

      const url = item.split(':-:').pop()?.trim();
      if (!url) {
        slide.hidden = true;
        continue;
      }

      const img = slide.querySelector('img');
      if (img) {
        img.src = `${url}&width=${maxWidth}`;
        img.srcset = widths.map((w) => `${url}&width=${w} ${w}w`).join(', ');
        img.dataset.maxResolution = `${url}&width=${maxWidth}`;
        img.removeAttribute('loading');
      }

      slide.hidden = false;
    }

    // Navigate to the first visible slide
    const firstVisibleSlide = /** @type {HTMLElement | undefined} */ (
      Array.from(slides).find((s) => !/** @type {HTMLElement} */ (s).hidden)
    );
    if (firstVisibleSlide) {
      slideshow.select?.({ id: firstVisibleSlide.getAttribute('slide-id') }, undefined, { animate: false });
    }
  }

  /**
   * Rebuilds the size swatch fieldset from RTS and MTO size strings, combining them
   * (RTS first, then any MTO sizes not already in RTS) without an API fetch.
   * @param {string | undefined} rtsSizesStr - Comma-separated RTS size values.
   * @param {string | undefined} mtoSizesStr - Comma-separated MTO size values.
   */
  #updateSizeSwatches(rtsSizesStr, mtoSizesStr) {
    const sizeFieldset = this.querySelector('fieldset.size');
    if (!sizeFieldset) return;

    const rtsSizes = rtsSizesStr ? rtsSizesStr.split(',').filter(Boolean) : [];
    const mtoSizes = mtoSizesStr ? mtoSizesStr.split(',').filter(Boolean) : [];

    // Combine: RTS first, then MTO additions not already present
    const allSizes = [...rtsSizes];
    for (const sz of mtoSizes) {
      if (!allSizes.includes(sz)) allSizes.push(sz);
    }

    // Keep the current size selected if the new color offers it; otherwise select the first size
    const previouslySelected = /** @type {HTMLInputElement | null} */ (
      sizeFieldset.querySelector('input[type="radio"]:checked')
    )?.value;
    const trimmedSizes = allSizes.map((sz) => (sz ?? '').trim());
    const sizeToSelect =
      previouslySelected && trimmedSizes.includes(previouslySelected) ? previouslySelected : trimmedSizes[0];

    if (allSizes.length === 0) {
      /** @type {HTMLElement} */ (sizeFieldset).hidden = true;
      return;
    }
    /** @type {HTMLElement} */ (sizeFieldset).hidden = false;

    const overflowList = sizeFieldset.querySelector('overflow-list');
    if (!overflowList) return;

    // Derive product ID before removing existing items
    const productId = this.dataset.productId ?? '';

    // Remove existing size items; preserve only the [slot="more"] overflow button.
    // Items that overflowed will have slot="overflow" set by OverflowList, so we must
    // compare against "more" explicitly rather than checking hasAttribute('slot').
    for (const el of Array.from(overflowList.children)) {
      if (el.getAttribute('slot') !== 'more') el.remove();
    }

    const moreSlot = overflowList.querySelector('[slot="more"]');

    for (let i = 0; i < allSizes.length; i++) {
      const sz = (allSizes[i] ?? '').trim();

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `Size-${productId}-swatch`;
      input.value = sz;
      input.setAttribute('aria-label', sz);
      input.dataset.inputId = `size-${i}`;
      input.checked = sz === sizeToSelect;

      const label = document.createElement('label');
      label.className =
        'variant-option__button-label variant-option__button-label--has-swatch swatch-rounded';
      label.append(input, document.createTextNode(sz));

      const li = document.createElement('li');
      li.className = 'variant-option__swatch size';
      li.appendChild(label);

      if (moreSlot) {
        overflowList.insertBefore(li, moreSlot);
      } else {
        overflowList.appendChild(li);
      }
    }

    // Trigger overflow list reflow so the counter recalculates
    requestAnimationFrame(() => {
      overflowList.dispatchEvent(new Event('reflow'));
    });
  }

  /**
   * Updates the product card price display using prices embedded in a color swatch's data-prices
   * attribute. Prices are dollar amounts from the SearchSpring price metafield (e.g. "1200" = $1,200).
   * @param {string} price - Regular/sale price in dollars.
   * @param {string} compareAt - Compare-at price in dollars ("0" means no sale).
   */
  #updatePriceFromSwatchData(price, compareAt) {
    const productCard = this.parentProductCard;
    if (!productCard) return;

    const priceContainers = productCard.querySelectorAll(`product-price [ref='priceContainer']`);
    const priceContainer = priceContainers[1] ?? priceContainers[0];
    if (!priceContainer) return;

    /** @param {string} dollarAmount @returns {string} */
    const formatPrice = (dollarAmount) => {
      const num = parseFloat(dollarAmount);
      if (!Number.isFinite(num) || num <= 0) return '';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    };

    const formattedPrice = formatPrice(price);
    if (!formattedPrice) return;

    const priceNum = parseFloat(price);
    const compareAtNum = parseFloat(compareAt);
    const showCompareAt = Number.isFinite(compareAtNum) && compareAtNum > 0 && compareAtNum > priceNum;

    let html = '';
    if (showCompareAt) {
      const formattedCompareAt = formatPrice(compareAt);
      html += `<span role="group"><span class="visually-hidden">Sale price&nbsp;</span><span class="price">${formattedPrice}</span></span>`;
      html += ` <span role="group"><span class="visually-hidden">Regular price&nbsp;</span><span class="compare-at-price font-normal">${formattedCompareAt}</span></span>`;
    } else {
      html = `<span class="price">${formattedPrice}</span>`;
    }

    priceContainer.innerHTML = html;
  }

  /**
   * Gets the correct product URL based on selected size and color's RTS availability.
   * Falls back to MTO URL if the size is not available in RTS.
   * @returns {string | null} The product URL to use
   */
  #getProductUrlForSelectedOptions() {
    // Find the selected color swatch
    const selectedColorSwatch = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][name*="Color"]:checked, input[type="radio"][name*="color"]:checked')
    );

    if (!selectedColorSwatch) return null;

    const rtsUrl = selectedColorSwatch.dataset.rtsUrl;
    const mtoUrl = selectedColorSwatch.dataset.mtoUrl;
    const rtsSizes = selectedColorSwatch.dataset.rtsSizes?.split(',') || [];

    // If no RTS URL, use MTO
    if (!rtsUrl) return mtoUrl || null;

    // If no MTO URL, use RTS
    if (!mtoUrl) return rtsUrl;

    // Find the selected size
    const selectedSizeSwatch = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][name*="Size"]:checked, input[type="radio"][name*="size"]:checked')
    );

    if (!selectedSizeSwatch) return rtsUrl;

    const selectedSize = selectedSizeSwatch.value;

    // Check if the selected size is available in RTS
    if (rtsSizes.includes(selectedSize)) {
      return rtsUrl;
    }

    // Size not in RTS, use MTO
    return mtoUrl;
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives
   * and RTS/MTO fallback based on size availability.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = /** @type {HTMLInputElement} */ (event.target);
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Local fast-path: color swatch with embedded gallery data — update card directly, skip API fetch
    const isColorSwatch = isSwatchInput && clickedSwatch.name?.toLowerCase().includes('color');
    if (isColorSwatch && clickedSwatch.dataset.gallery) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);
      this.dispatchEvent(new VariantSelectedEvent({ id: clickedSwatch.dataset.optionValueId ?? '' }));
      this.#applySwatchDataLocally(clickedSwatch);
      return;
    }

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    const isSizeSwatch = isSwatchInput && clickedSwatch.name?.toLowerCase().includes('size');

    if (isSizeSwatch) {
      // Local fast-path: if the selected color has embedded gallery data, update the card link
      // directly without an API fetch (gallery already reflects the correct color).
      const selectedColorSwatch = /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[type="radio"][name*="Color"]:checked, input[type="radio"][name*="color"]:checked')
      );
      if (selectedColorSwatch?.dataset.gallery) {
        this.updateSelectedOption(clickedSwatch);
        const correctUrl = this.#getProductUrlForSelectedOptions();
        if (correctUrl && this.parentProductCard instanceof ProductCard) {
          this.parentProductCard.refs.productCardLink.href = correctUrl;
          const galleryLink = /** @type {HTMLAnchorElement | null} */ (
            this.parentProductCard.querySelector('[ref="cardGalleryLink"]')
          );
          if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = correctUrl;
        }
        return;
      }

      // Get the correct URL based on RTS/MTO availability
      const correctUrl = this.#getProductUrlForSelectedOptions();

      if (correctUrl) {
        // Update selection only when we're handling the update ourselves
        this.updateSelectedOption(clickedSwatch);

        // Dispatch event for listeners
        this.dispatchEvent(
          new VariantSelectedEvent({ id: clickedSwatch.dataset.optionValueId ?? '' })
        );

        // Build request URL using the URL API to avoid malformed URLs when correctUrl already contains query params
        try {
          const url = new URL(correctUrl, window.location.origin);
          url.searchParams.set('section_id', 'section-rendering-product-card');
          this.fetchUpdatedSection(url.href);
        } catch (error) {
          // Fallback to concatenation if URL constructor fails for some reason
          const separator = correctUrl.includes('?') ? '&' : '?';
          this.fetchUpdatedSection(`${correctUrl}${separator}section_id=section-rendering-product-card`);
        }
        return;
      } else {
        // No RTS/MTO URL available. Handle locally to avoid duplicate updateSelectedOption in parent.
        // Do NOT call updateSelectedOption here; let the radio's native checked state reflect the change.
        // Dispatch the variant selected event and fetch using the standard request builder.
        this.dispatchEvent(
          new VariantSelectedEvent({ id: clickedSwatch.dataset.optionValueId ?? '' })
        );

        // Use parent's request builder to keep behavior consistent with VariantPicker
        const requestUrl = this.buildRequestUrl(clickedSwatch);
        this.fetchUpdatedSection(requestUrl);
        return;
      }
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    if (!event || !(event.target instanceof Element)) return;

    const overflowList = event.target.closest('overflow-list');

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
